import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { permission } from '@nohara-dxdev/oidc-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { JSONSchema7 } from 'json-schema';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettingValues } from '@/features/settings';

import {
  useItemTemplateSchema,
  useItemTemplateVersions,
  useUpdateItemTemplate,
  UpdateItemTemplateDto,
} from '../api';
import { getRoleTagTranslationKey } from '../utils/project-role-tags';

import { RequiredInfrastructureDisplay } from './required-infrastructure-display';

export interface ItemTemplateSchemaDetailProps {
  itemTemplateId: string;
  /** Suppress editing affordances (Edit button + edit dialog). Used by the preview pane. */
  readOnly?: boolean;
}

interface AttributeRow {
  key: string;
  name: string;
  inputFieldType: string;
  options: string[];
  unit: string;
  order: number;
}

interface EditableAttribute {
  key: string;
  title: string;
  type: 'text' | 'dropdown' | 'number';
  options: string;
  unit: string;
  order: number;
}

function parseAttributesFromSchema(jsonSchema: JSONSchema7 | undefined): AttributeRow[] {
  if (jsonSchema?.type !== 'object' || !jsonSchema.properties) return [];
  return Object.entries(jsonSchema.properties)
    .map(([key, property]: [string, any]) => {
      const name = property?.title ?? key;
      let inputFieldType = 'Text';
      let options: string[] = [];
      const unit = property['x-unit'] ?? '';

      if (property.type === 'string' && property.enum && Array.isArray(property.enum)) {
        inputFieldType = 'Dropdown';
        options = property.enum;
      } else if (property.type === 'number' || property.type === 'integer') {
        inputFieldType = 'Number';
      }

      return {
        key,
        name,
        inputFieldType,
        options,
        unit,
        order: property['x-order'] ?? Number.MAX_SAFE_INTEGER,
      };
    })
    .sort((a, b) => a.order - b.order);
}

function getAttrKey(attr: EditableAttribute): string {
  return attr.key || attr.title.toLowerCase().replace(/\s+/g, '_');
}

function buildJsonSchemaFromAttributes(
  attrs: EditableAttribute[],
  baseSchema: JSONSchema7 | null | undefined,
): JSONSchema7 {
  const base = baseSchema ?? {};
  const properties: Record<string, JSONSchema7> = {};
  const seenKeys = new Set<string>();
  attrs.forEach((attr, idx) => {
    const computedKey = getAttrKey(attr);
    if (!computedKey || seenKeys.has(computedKey)) return; // skip blank or duplicate keys
    const prop: JSONSchema7 & { 'x-unit'?: string; 'x-order'?: number } = {
      title: attr.title,
      'x-order': idx,
    };
    if (attr.type === 'dropdown') {
      const opts = attr.options
        .split(',')
        .map((o) => o.trim())
        .filter(Boolean);
      if (opts.length === 0) return; // skip dropdowns with no usable options
      prop.type = 'string';
      prop.enum = opts;
    } else if (attr.type === 'number') {
      prop.type = 'number';
    } else {
      prop.type = 'string';
    }
    if (attr.unit) prop['x-unit'] = attr.unit;
    seenKeys.add(computedKey);
    properties[computedKey] = prop;
  });
  const currentKeys = new Set(Object.keys(properties));
  const required = Array.isArray(base.required)
    ? base.required.filter((r) => currentKeys.has(r))
    : base.required;
  return { ...base, type: 'object', properties, required };
}

const updateItemTemplatePermissions = [permission.itemTemplate.update];

export const ItemTemplateSchemaDetail = ({
  itemTemplateId,
  readOnly = false,
}: ItemTemplateSchemaDetailProps) => {
  const { t } = useTranslation();
  const { getSettingEntityValue } = useSettingValues();
  const scopeIds = useScopeIds();
  const subject = useMemo(() => ({ ...scopeIds, itemTemplateId }), [scopeIds, itemTemplateId]);

  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);
  const [versionMenuAnchor, setVersionMenuAnchor] = useState<HTMLElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editTemplateKey, setEditTemplateKey] = useState('');
  const [editAttributes, setEditAttributes] = useState<EditableAttribute[]>([]);

  const {
    data: templateSchema,
    isLoading,
    error,
  } = useItemTemplateSchema(itemTemplateId, {
    version: selectedVersion,
  });
  const { data: versionsData } = useItemTemplateVersions(itemTemplateId);
  const updateMutation = useUpdateItemTemplate(itemTemplateId);

  const itemType = useMemo(() => {
    const id = templateSchema?.itemTypeId;
    return id ? getSettingEntityValue<string>(id) : undefined;
  }, [templateSchema?.itemTypeId, getSettingEntityValue]);

  const constructionTypes = useMemo(() => {
    const ids = templateSchema?.constructionTypeIds;
    return ids?.length ? ids.map((id) => getSettingEntityValue(id)) : [];
  }, [templateSchema?.constructionTypeIds, getSettingEntityValue]);

  const attributesData = useMemo(
    () => parseAttributesFromSchema(templateSchema?.jsonSchema),
    [templateSchema],
  );

  const currentVersion = versionsData?.currentVersion ?? templateSchema?.itemTemplateVersion ?? 1;
  const isViewingCurrent = selectedVersion == null || selectedVersion === currentVersion;

  const hasAttributeErrors = useMemo(() => {
    const seenKeys = new Set<string>();
    return editAttributes.some((attr) => {
      const key = getAttrKey(attr);
      if (!key) return true;
      if (seenKeys.has(key)) return true;
      seenKeys.add(key);
      if (attr.type === 'dropdown') {
        const opts = attr.options
          .split(',')
          .map((o) => o.trim())
          .filter(Boolean);
        if (opts.length === 0) return true;
      }
      return false;
    });
  }, [editAttributes]);

  function openEditDialog() {
    if (!templateSchema) return;
    setEditName(templateSchema.name);
    setEditTemplateKey(templateSchema.templateKey);
    setEditAttributes(
      attributesData.map((row) => ({
        key: row.key,
        title: row.name,
        type:
          row.inputFieldType === 'Dropdown'
            ? 'dropdown'
            : row.inputFieldType === 'Number'
              ? 'number'
              : 'text',
        options: row.options.join(', '),
        unit: row.unit,
        order: row.order,
      })),
    );
    setEditOpen(true);
  }

  function addAttribute() {
    setEditAttributes((prev) => [
      ...prev,
      { key: '', title: '', type: 'text', options: '', unit: '', order: prev.length },
    ]);
  }

  function removeAttribute(idx: number) {
    setEditAttributes((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateAttribute(idx: number, field: keyof EditableAttribute, value: string) {
    setEditAttributes((prev) => prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
  }

  async function handleSave() {
    if (!templateSchema) return;
    const newSchema = buildJsonSchemaFromAttributes(editAttributes, templateSchema.jsonSchema);
    const payload: UpdateItemTemplateDto = {
      name: editName,
      templateKey: editTemplateKey,
      jsonSchema: newSchema,
    };
    await updateMutation.mutateAsync(payload);
    setConfirmOpen(false);
    setEditOpen(false);
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  if (error || !templateSchema) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">{t('itemTemplates.schemaDetail.error')}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Template Schema Info */}
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h5" gutterBottom>
                {templateSchema.name || t('itemTemplates.schemaDetail.untitled')}
              </Typography>
              {/* Version chip — click to open version picker when multiple versions exist */}
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                {(() => {
                  const activeVersion = selectedVersion ?? currentVersion;
                  const hasMultiple = (versionsData?.versions.length ?? 1) > 1;
                  return (
                    <>
                      <Chip
                        label={t('itemTemplates.version.label', { version: activeVersion })}
                        size="small"
                        color="primary"
                        variant="filled"
                        {...(hasMultiple && {
                          clickable: true,
                          onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                            setVersionMenuAnchor(e.currentTarget);
                          },
                        })}
                      />
                      <Menu
                        anchorEl={versionMenuAnchor}
                        open={Boolean(versionMenuAnchor)}
                        onClose={() => {
                          setVersionMenuAnchor(null);
                        }}
                        slotProps={{ paper: { sx: { p: 1.5 } } }}
                      >
                        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, maxWidth: 280 }}>
                          {(versionsData?.versions ?? []).map((v) => (
                            <Chip
                              key={v.version}
                              label={t('itemTemplates.version.label', { version: v.version })}
                              size="small"
                              color={v.version === activeVersion ? 'primary' : 'default'}
                              variant={v.version === activeVersion ? 'filled' : 'outlined'}
                              clickable
                              onClick={() => {
                                setSelectedVersion(
                                  v.version === currentVersion ? undefined : v.version,
                                );
                                setVersionMenuAnchor(null);
                              }}
                            />
                          ))}
                        </Stack>
                      </Menu>
                    </>
                  );
                })()}
              </Stack>
            </Box>

            {/* Edit button — only visible for current version, gated by itemTemplate:update */}
            {!readOnly && isViewingCurrent && (
              <OidcProtectedComponent requireAll={updateItemTemplatePermissions} subject={subject}>
                <Tooltip title={t('itemTemplates.edit.button')}>
                  <Button variant="contained" startIcon={<EditIcon />} onClick={openEditDialog}>
                    {t('itemTemplates.edit.button')}
                  </Button>
                </Tooltip>
              </OidcProtectedComponent>
            )}
          </Stack>

          <Stack spacing={2} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('itemTemplates.schemaDetail.itemName')}
                </Typography>
                <Typography variant="body1">{templateSchema.name || '-'}</Typography>
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('itemTemplates.schemaDetail.itemCharacter')}
                </Typography>
                <Typography variant="body1">{templateSchema.templateKey || '-'}</Typography>
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('itemTemplates.schemaDetail.itemType')}
                </Typography>
                <Typography variant="body1">{itemType ?? '-'}</Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  {t('itemTemplates.schemaDetail.constructionTypes')}
                </Typography>
                {constructionTypes.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {constructionTypes.map((type, index) => (
                      <Chip
                        key={index}
                        label={String(type)}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body1">-</Typography>
                )}
              </Box>
              <Box sx={{ minWidth: 300 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  {t('itemTemplates.schemaDetail.projectRoles')}
                </Typography>
                {templateSchema.projectRoleTags.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {templateSchema.projectRoleTags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={t(getRoleTagTranslationKey(tag))}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body1">-</Typography>
                )}
              </Box>
            </Stack>

            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('itemTemplates.schemaDetail.isBoardItem')}
                </Typography>
                <Chip
                  label={templateSchema.isBoardItem ? t('common.yes') : t('common.no')}
                  size="small"
                  color={templateSchema.isBoardItem ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('itemTemplates.schemaDetail.canConnectToBoard')}
                </Typography>
                <Chip
                  label={templateSchema.canConnectToBoard ? t('common.yes') : t('common.no')}
                  size="small"
                  color={templateSchema.canConnectToBoard ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>
            </Stack>

            {templateSchema.infrastructureRequirements &&
              templateSchema.infrastructureRequirements.length > 0 && (
                <RequiredInfrastructureDisplay
                  infrastructureRequirements={templateSchema.infrastructureRequirements}
                />
              )}
          </Stack>
        </CardContent>
      </Card>

      {/* Attributes Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('itemTemplates.schemaDetail.attributes')}
          </Typography>

          {attributesData.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('itemTemplates.schemaDetail.attributeName')}</TableCell>
                    <TableCell>{t('itemTemplates.schemaDetail.inputFieldType')}</TableCell>
                    <TableCell>{t('itemTemplates.schemaDetail.options')}</TableCell>
                    <TableCell>{t('itemTemplates.schemaDetail.unit')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attributesData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.inputFieldType}
                          size="small"
                          variant="outlined"
                          color={
                            row.inputFieldType === 'Dropdown'
                              ? 'primary'
                              : row.inputFieldType === 'Number'
                                ? 'secondary'
                                : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {row.options.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {row.options.map((option, optionIndex) => (
                              <Chip
                                key={optionIndex}
                                label={option}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ mb: 0.5 }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          t('common.none')
                        )}
                      </TableCell>
                      <TableCell>{row.unit || t('common.none')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              {t('itemTemplates.schemaDetail.noAttributes')}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog — never opens in read-only mode (Edit button is hidden) */}
      <Dialog
        open={!readOnly && editOpen}
        onClose={() => {
          setEditOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('itemTemplates.edit.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('itemTemplates.edit.name')}
              value={editName}
              onChange={(e) => {
                setEditName(e.target.value);
              }}
              fullWidth
              required
            />
            <TextField
              label={t('itemTemplates.edit.templateKey')}
              value={editTemplateKey}
              onChange={(e) => {
                setEditTemplateKey(e.target.value);
              }}
              fullWidth
              required
            />

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle1">{t('itemTemplates.edit.attributes')}</Typography>
                <Button startIcon={<AddIcon />} onClick={addAttribute} size="small">
                  {t('itemTemplates.edit.addAttribute')}
                </Button>
              </Stack>

              <Stack spacing={2}>
                {editAttributes.map((attr, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <TextField
                        label={t('itemTemplates.edit.attributeName')}
                        value={attr.title}
                        onChange={(e) => {
                          updateAttribute(idx, 'title', e.target.value);
                        }}
                        size="small"
                        sx={{ flex: 2 }}
                      />
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel>{t('itemTemplates.edit.attributeType')}</InputLabel>
                        <Select
                          value={attr.type}
                          label={t('itemTemplates.edit.attributeType')}
                          onChange={(e) => {
                            updateAttribute(idx, 'type', e.target.value);
                          }}
                        >
                          <MenuItem value="text">{t('itemTemplates.edit.typeText')}</MenuItem>
                          <MenuItem value="number">{t('itemTemplates.edit.typeNumber')}</MenuItem>
                          <MenuItem value="dropdown">
                            {t('itemTemplates.edit.typeDropdown')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                      {attr.type === 'dropdown' && (
                        <TextField
                          label={t('itemTemplates.edit.attributeOptions')}
                          value={attr.options}
                          onChange={(e) => {
                            updateAttribute(idx, 'options', e.target.value);
                          }}
                          size="small"
                          sx={{ flex: 2 }}
                          placeholder={t('itemTemplates.edit.optionsPlaceholder')}
                        />
                      )}
                      <TextField
                        label={t('itemTemplates.schemaDetail.unit')}
                        value={attr.unit}
                        onChange={(e) => {
                          updateAttribute(idx, 'unit', e.target.value);
                        }}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          removeAttribute(idx);
                        }}
                        color="error"
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditOpen(false);
            }}
            disabled={updateMutation.isPending}
          >
            {t('itemTemplates.edit.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(true);
            }}
            disabled={
              updateMutation.isPending ||
              !editName.trim() ||
              !editTemplateKey.trim() ||
              hasAttributeErrors
            }
            startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('itemTemplates.edit.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update confirmation modal — shown before submitting the edit form */}
      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!updateMutation.isPending) {
            setConfirmOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('itemTemplates.updateConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {t('itemTemplates.updateConfirm.intro')}
            <Box component="ul" sx={{ my: 1, pl: 3 }}>
              <li>{t('itemTemplates.updateConfirm.bulletInProgress')}</li>
              <li>{t('itemTemplates.updateConfirm.bulletUnlocked')}</li>
              <li>{t('itemTemplates.updateConfirm.bulletPreserve')}</li>
            </Box>
            {t('itemTemplates.updateConfirm.question')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
            }}
            disabled={updateMutation.isPending}
          >
            {t('itemTemplates.updateConfirm.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={updateMutation.isPending}
            startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('itemTemplates.updateConfirm.apply')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
